'use client'

import type { SalesByDayPoint } from '@mentora/data-access'
import type { ChartConfig } from '@mentora/ui-components'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@mentora/ui-components'
import { useFormatter, useTranslations } from 'next-intl'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'

/**
 * Confirmed sales per day, stacked by what was sold. Without `showTickets` it
 * is course enrollments alone, labelled as such.
 */
export function SalesChart({
  points,
  showTickets,
}: {
  points: SalesByDayPoint[]
  showTickets: boolean
}) {
  const t = useTranslations('dashboard.charts')
  const format = useFormatter()

  const chartConfig: ChartConfig = {
    courseCount: { label: showTickets ? t('courses') : t('enrollments'), color: 'var(--chart-1)' },
    ticketCount: { label: t('tickets'), color: 'var(--chart-2)' },
  }

  return (
    <ChartContainer config={chartConfig} className="aspect-auto h-60 w-full">
      <BarChart data={points} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          minTickGap={32}
          tickFormatter={value =>
            format.dateTime(new Date(`${value}T12:00:00`), { day: '2-digit', month: '2-digit' })}
        />
        <YAxis tickLine={false} axisLine={false} width={32} allowDecimals={false} />
        <ChartTooltip
          content={(
            <ChartTooltipContent
              indicator="dashed"
              labelFormatter={value =>
                format.dateTime(new Date(`${String(value)}T12:00:00`), {
                  day: '2-digit',
                  month: 'long',
                })}
            />
          )}
        />
        <Bar
          dataKey="courseCount"
          stackId="sales"
          fill="var(--color-courseCount)"
          radius={showTickets ? [0, 0, 0, 0] : [4, 4, 0, 0]}
        />
        {showTickets && (
          <Bar
            dataKey="ticketCount"
            stackId="sales"
            fill="var(--color-ticketCount)"
            radius={[4, 4, 0, 0]}
          />
        )}
      </BarChart>
    </ChartContainer>
  )
}
